import React, { useState, useEffect, useCallback } from 'react';
import { empleadosApi, tiendasApi, ordenesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const initialEquipo = {
  descripcion: '',
  marca: '',
  numero_serie: '',
  accesorios: '',
  falla: '',
  datos_acceso: ''
};

const initialServicio = {
  descripcion: '',
  cantidad: 1,
  precio_unitario: 0,
  subtotal: 0,
  notas: ''
};

const initialMercaderia = {
  descripcion: '',
  cantidad: 1,
  precio_unitario: 0,
  subtotal: 0,
  notas: ''
};

const initialRepuesto = {
  descripcion: '',
  cantidad: 1,
  precio_compra: 0,
  precio_venta: 0,
  subtotal_compra: 0,
  subtotal_venta: 0,
  lugar_cotizacion: '',
  fecha_ingreso: '',
  notas: ''
};

const initialFormData = {
  fecha_recepcion: new Date().toISOString().split('T')[0],
  nombre_cliente: '',
  telefono_cliente: '',
  correo_cliente: '',

  tipo_servicio: 'Revision',
  detalle_trabajo: '',
  observaciones: '',
  condicion_fisica: '',

  mercaderia: '',
  precio_mercaderia: 0,
  repuesto: '',
  lugar_cotizacion: '',
  fecha_ingreso_repuesto: '',
  costo_repuesto: 0,

  costo_trabajo: 0,
  anticipo: 0,
  fecha_anticipo: '',
  tipo_pago: 'Pendiente',

  status: 'pendiente',
  fase: 'recepcion',
  fecha_entrega: '',
  recibido_por: '',
  tecnico_asignado: '',
  tienda_id: 1,

  // Campos de revisión técnico
  fecha_revision: '',
  tecnico_reviso: '',
  diagnostico: '',
  necesita_mercaderia: false
};

const OrdenForm = ({ initialData, onSubmit, loading }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState(initialFormData);
  const [equipos, setEquipos] = useState([{ ...initialEquipo }]);
  const [servicios, setServicios] = useState([]);
  const [mercaderias, setMercaderias] = useState([]);
  const [repuestos, setRepuestos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [tiendas, setTiendas] = useState([]);
  const [buscandoCliente, setBuscandoCliente] = useState(false);
  const [clienteEncontrado, setClienteEncontrado] = useState(null);

  useEffect(() => {
    loadCatalogs();
  }, []);

  // Auto-set recibido_por y tienda_id cuando el usuario está logueado (solo para órdenes nuevas)
  useEffect(() => {
    if (user && !initialData) {
      setFormData(prev => ({
        ...prev,
        recibido_por: user.id,
        tienda_id: user.tienda_id || prev.tienda_id
      }));
    }
  }, [user, initialData]);

  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialFormData, ...initialData });
      // Cargar equipos si existen
      if (initialData.equipos && initialData.equipos.length > 0) {
        setEquipos(initialData.equipos);
      } else {
        // Construir equipos desde campos legacy
        const equiposLegacy = [];
        if (initialData.equipo1_descripcion) {
          equiposLegacy.push({
            descripcion: initialData.equipo1_descripcion || '',
            marca: initialData.equipo1_marca || '',
            numero_serie: initialData.equipo1_numero_serie || '',
            accesorios: initialData.equipo1_accesorios || '',
            falla: initialData.equipo1_falla || '',
            datos_acceso: initialData.equipo1_datos_acceso || ''
          });
        }
        if (initialData.equipo2_descripcion) {
          equiposLegacy.push({
            descripcion: initialData.equipo2_descripcion || '',
            marca: initialData.equipo2_marca || '',
            numero_serie: initialData.equipo2_numero_serie || '',
            accesorios: initialData.equipo2_accesorios || '',
            falla: initialData.equipo2_falla || '',
            datos_acceso: initialData.equipo2_datos_acceso || ''
          });
        }
        if (initialData.equipo3_descripcion) {
          equiposLegacy.push({
            descripcion: initialData.equipo3_descripcion || '',
            marca: initialData.equipo3_marca || '',
            numero_serie: initialData.equipo3_numero_serie || '',
            accesorios: initialData.equipo3_accesorios || '',
            falla: initialData.equipo3_falla || '',
            datos_acceso: initialData.equipo3_datos_acceso || ''
          });
        }
        if (equiposLegacy.length > 0) {
          setEquipos(equiposLegacy);
        }
      }

      // Cargar servicios, mercaderías y repuestos
      if (initialData.servicios && initialData.servicios.length > 0) {
        setServicios(initialData.servicios);
      }
      if (initialData.mercaderias && initialData.mercaderias.length > 0) {
        setMercaderias(initialData.mercaderias);
      }
      if (initialData.repuestos && initialData.repuestos.length > 0) {
        setRepuestos(initialData.repuestos);
      }
    }
  }, [initialData]);

  const loadCatalogs = async () => {
    try {
      const [empRes, tiendasRes] = await Promise.all([
        empleadosApi.getAll({ activos: '1' }),
        tiendasApi.getAll()
      ]);
      setEmpleados(empRes.data || []);
      setTiendas(tiendasRes.data || []);
    } catch (error) {
      console.error('Error cargando catálogos:', error);
    }
  };

  const buscarCliente = useCallback(async (telefono) => {
    if (!telefono || telefono.length < 8) {
      setClienteEncontrado(null);
      return;
    }

    setBuscandoCliente(true);
    try {
      const response = await ordenesApi.buscarCliente(telefono);
      if (response.found && response.cliente) {
        setClienteEncontrado(response.cliente);
        setFormData(prev => ({
          ...prev,
          nombre_cliente: response.cliente.nombre,
          correo_cliente: response.cliente.correo_electronico || ''
        }));
      } else {
        setClienteEncontrado(null);
      }
    } catch (error) {
      console.error('Error buscando cliente:', error);
      setClienteEncontrado(null);
    } finally {
      setBuscandoCliente(false);
    }
  }, []);

  const handleTelefonoBlur = () => {
    if (formData.telefono_cliente && !initialData) {
      buscarCliente(formData.telefono_cliente);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
      };

      // Al cambiar de sucursal, limpiar técnico si no pertenece a la nueva sucursal
      if (name === 'tienda_id' && prev.tecnico_asignado) {
        const tecnicoActual = empleados.find(e => String(e.id) === String(prev.tecnico_asignado));
        if (tecnicoActual && String(tecnicoActual.tienda_id) !== String(value)) {
          updated.tecnico_asignado = '';
        }
      }

      return updated;
    });
  };

  const handleEquipoChange = (index, field, value) => {
    setEquipos(prev => {
      const newEquipos = [...prev];
      newEquipos[index] = { ...newEquipos[index], [field]: value };
      return newEquipos;
    });
  };

  const agregarEquipo = () => {
    setEquipos(prev => [...prev, { ...initialEquipo }]);
  };

  const eliminarEquipo = (index) => {
    if (equipos.length > 1) {
      setEquipos(prev => prev.filter((_, i) => i !== index));
    }
  };

  // Funciones para manejar servicios
  const handleServicioChange = (index, field, value) => {
    setServicios(prev => {
      const newServicios = [...prev];
      newServicios[index] = { ...newServicios[index], [field]: value };

      // Calcular subtotal automáticamente
      if (field === 'cantidad' || field === 'precio_unitario') {
        const cantidad = field === 'cantidad' ? parseFloat(value) || 0 : parseFloat(newServicios[index].cantidad) || 0;
        const precioUnitario = field === 'precio_unitario' ? parseFloat(value) || 0 : parseFloat(newServicios[index].precio_unitario) || 0;
        newServicios[index].subtotal = cantidad * precioUnitario;
      }

      return newServicios;
    });
  };

  const agregarServicio = () => {
    setServicios(prev => [...prev, { ...initialServicio }]);
  };

  const eliminarServicio = (index) => {
    setServicios(prev => prev.filter((_, i) => i !== index));
  };

  // Funciones para manejar mercaderías
  const handleMercaderiaChange = (index, field, value) => {
    setMercaderias(prev => {
      const newMercaderias = [...prev];
      newMercaderias[index] = { ...newMercaderias[index], [field]: value };

      // Calcular subtotal automáticamente
      if (field === 'cantidad' || field === 'precio_unitario') {
        const cantidad = field === 'cantidad' ? parseFloat(value) || 0 : parseFloat(newMercaderias[index].cantidad) || 0;
        const precioUnitario = field === 'precio_unitario' ? parseFloat(value) || 0 : parseFloat(newMercaderias[index].precio_unitario) || 0;
        newMercaderias[index].subtotal = cantidad * precioUnitario;
      }

      return newMercaderias;
    });
  };

  const agregarMercaderia = () => {
    setMercaderias(prev => [...prev, { ...initialMercaderia }]);
  };

  const eliminarMercaderia = (index) => {
    setMercaderias(prev => prev.filter((_, i) => i !== index));
  };

  // Funciones para manejar repuestos
  const handleRepuestoChange = (index, field, value) => {
    setRepuestos(prev => {
      const newRepuestos = [...prev];
      newRepuestos[index] = { ...newRepuestos[index], [field]: value };

      // Calcular subtotales automáticamente
      if (field === 'cantidad' || field === 'precio_compra' || field === 'precio_venta') {
        const cantidad = field === 'cantidad' ? parseFloat(value) || 0 : parseFloat(newRepuestos[index].cantidad) || 0;
        const precioCompra = field === 'precio_compra' ? parseFloat(value) || 0 : parseFloat(newRepuestos[index].precio_compra) || 0;
        const precioVenta = field === 'precio_venta' ? parseFloat(value) || 0 : parseFloat(newRepuestos[index].precio_venta) || 0;
        newRepuestos[index].subtotal_compra = cantidad * precioCompra;
        newRepuestos[index].subtotal_venta = cantidad * precioVenta;
      }

      return newRepuestos;
    });
  };

  const agregarRepuesto = () => {
    setRepuestos(prev => [...prev, { ...initialRepuesto }]);
  };

  const eliminarRepuesto = (index) => {
    setRepuestos(prev => prev.filter((_, i) => i !== index));
  };

  // Calcular totales incluyendo servicios, mercaderías y repuestos
  const calculateTotals = () => {
    const precioMercaderiaLegacy = parseFloat(formData.precio_mercaderia) || 0;
    const costoTrabajo = parseFloat(formData.costo_trabajo) || 0;
    const anticipo = parseFloat(formData.anticipo) || 0;

    // Sumar servicios
    const totalServicios = servicios.reduce((sum, servicio) => sum + (parseFloat(servicio.subtotal) || 0), 0);

    // Sumar mercaderías
    const totalMercaderias = mercaderias.reduce((sum, mercaderia) => sum + (parseFloat(mercaderia.subtotal) || 0), 0);

    // Sumar repuestos (precio de venta, no compra)
    const totalRepuestos = repuestos.reduce((sum, repuesto) => sum + (parseFloat(repuesto.subtotal_venta) || 0), 0);

    // Total al cliente = servicios + mercaderías + repuestos (venta) + trabajo + mercadería legacy
    const valorTotal = totalServicios + totalMercaderias + totalRepuestos + costoTrabajo + precioMercaderiaLegacy;
    const saldo = valorTotal - anticipo;

    return { valorTotal, saldo };
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { valorTotal, saldo } = calculateTotals();

    // Convertir equipos al formato legacy (primeros 3) + array completo
    const dataToSubmit = {
      ...formData,
      valor_total: valorTotal,
      saldo: saldo,
      equipos: equipos.filter(eq => eq.descripcion), // Solo equipos con descripción
      // Campos legacy para compatibilidad
      equipo1_descripcion: equipos[0]?.descripcion || '',
      equipo1_marca: equipos[0]?.marca || '',
      equipo1_numero_serie: equipos[0]?.numero_serie || '',
      equipo1_accesorios: equipos[0]?.accesorios || '',
      equipo1_falla: equipos[0]?.falla || '',
      equipo1_datos_acceso: equipos[0]?.datos_acceso || '',
      equipo2_descripcion: equipos[1]?.descripcion || '',
      equipo2_marca: equipos[1]?.marca || '',
      equipo2_numero_serie: equipos[1]?.numero_serie || '',
      equipo2_accesorios: equipos[1]?.accesorios || '',
      equipo2_falla: equipos[1]?.falla || '',
      equipo2_datos_acceso: equipos[1]?.datos_acceso || '',
      equipo3_descripcion: equipos[2]?.descripcion || '',
      equipo3_marca: equipos[2]?.marca || '',
      equipo3_numero_serie: equipos[2]?.numero_serie || '',
      equipo3_accesorios: equipos[2]?.accesorios || '',
      equipo3_falla: equipos[2]?.falla || '',
      equipo3_datos_acceso: equipos[2]?.datos_acceso || '',
      // Nuevos campos: servicios, mercaderías y repuestos
      servicios: servicios.filter(s => s.descripcion), // Solo servicios con descripción
      mercaderias: mercaderias.filter(m => m.descripcion), // Solo mercaderías con descripción
      repuestos: repuestos.filter(r => r.descripcion), // Solo repuestos con descripción
    };

    onSubmit(dataToSubmit);
  };

  const { valorTotal, saldo } = calculateTotals();
  const isEditing = !!initialData;
  const isTecnico = user?.rol === 'tecnico';
  const isAdmin = user?.rol === 'admin';

  return (
    <form onSubmit={handleSubmit}>
      {/* FASE 1: Información del Cliente y Recepción */}
      <div className="form-section">
        <h3 className="form-section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Fase 1: Recepción - Datos del Cliente
        </h3>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Teléfono del Cliente *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                name="telefono_cliente"
                value={formData.telefono_cliente}
                onChange={handleChange}
                onBlur={handleTelefonoBlur}
                className="form-control"
                placeholder="Ingrese teléfono para buscar cliente"
                required
              />
              {buscandoCliente && (
                <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)' }}>
                  Buscando...
                </span>
              )}
            </div>
            {clienteEncontrado && (
              <small style={{ color: 'var(--success-color)' }}>Cliente encontrado en la base de datos</small>
            )}
            {formData.telefono_cliente && !clienteEncontrado && !buscandoCliente && (
              <small style={{ color: 'var(--info-color)' }}>Cliente nuevo - se registrará automáticamente</small>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Nombre del Cliente *</label>
            <input
              type="text"
              name="nombre_cliente"
              value={formData.nombre_cliente}
              onChange={handleChange}
              className="form-control"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Correo Electrónico</label>
            <input
              type="email"
              name="correo_cliente"
              value={formData.correo_cliente}
              onChange={handleChange}
              className="form-control"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Fecha de Recepción</label>
            <input
              type="date"
              name="fecha_recepcion"
              value={formData.fecha_recepcion}
              onChange={handleChange}
              className="form-control"
            />
          </div>
          <div className="form-group">
            <label className="form-label">Recibido Por</label>
            <select
              name="recibido_por"
              value={formData.recibido_por || ''}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">-- Seleccionar --</option>
              {empleados.filter(e => e.activo && String(e.tienda_id) === String(formData.tienda_id)).map(emp => (
                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
              ))}
            </select>
            <small style={{ color: 'var(--text-light)', fontSize: '11px' }}>Quién recibió físicamente el equipo (puede ser distinto a quien registra la orden)</small>
          </div>
          <div className="form-group">
            <label className="form-label">Sucursal</label>
            <select
              name="tienda_id"
              value={formData.tienda_id}
              onChange={handleChange}
              className="form-control"
              disabled={user?.rol !== 'admin'}
            >
              {tiendas.filter(t => t.activa).map(tienda => (
                <option key={tienda.id} value={tienda.id}>{tienda.nombre}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Tecnico Encargado</label>
            <select
              name="tecnico_asignado"
              value={formData.tecnico_asignado || ''}
              onChange={handleChange}
              className="form-control"
            >
              <option value="">-- Sin asignar --</option>
              {empleados.filter(e => e.rol === 'tecnico' && e.activo && String(e.tienda_id) === String(formData.tienda_id)).map(emp => (
                <option key={emp.id} value={emp.id}>{emp.nombre}</option>
              ))}
            </select>
            <small style={{ color: 'var(--text-light)', fontSize: '11px' }}>Tecnico responsable de la reparacion</small>
          </div>
        </div>
      </div>

      {/* Equipos Dinámicos */}
      <div className="form-section">
        <h3 className="form-section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
            <line x1="8" y1="21" x2="16" y2="21"/>
            <line x1="12" y1="17" x2="12" y2="21"/>
          </svg>
          Equipos a Recibir ({equipos.length})
        </h3>

        {equipos.map((equipo, index) => (
          <div key={index} style={{
            background: '#fff',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <strong>Equipo {index + 1}</strong>
              {equipos.length > 1 && (
                <button
                  type="button"
                  onClick={() => eliminarEquipo(index)}
                  className="btn btn-danger btn-sm"
                >
                  Eliminar
                </button>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Descripción del Equipo *</label>
                <input
                  type="text"
                  value={equipo.descripcion}
                  onChange={(e) => handleEquipoChange(index, 'descripcion', e.target.value)}
                  className="form-control"
                  placeholder="Ej: Laptop, Impresora, PC de escritorio"
                  required={index === 0}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Marca</label>
                <input
                  type="text"
                  value={equipo.marca}
                  onChange={(e) => handleEquipoChange(index, 'marca', e.target.value)}
                  className="form-control"
                  placeholder="Ej: HP, Dell, Canon"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Número de Serie</label>
                <input
                  type="text"
                  value={equipo.numero_serie}
                  onChange={(e) => handleEquipoChange(index, 'numero_serie', e.target.value)}
                  className="form-control"
                  placeholder="Ej: SN123456789"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Accesorios</label>
                <input
                  type="text"
                  value={equipo.accesorios}
                  onChange={(e) => handleEquipoChange(index, 'accesorios', e.target.value)}
                  className="form-control"
                  placeholder="Ej: Cargador, mouse, funda"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Datos de Acceso</label>
                <input
                  type="text"
                  value={equipo.datos_acceso}
                  onChange={(e) => handleEquipoChange(index, 'datos_acceso', e.target.value)}
                  className="form-control"
                  placeholder="Contraseña de Windows, etc."
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Falla / Problema</label>
              <textarea
                value={equipo.falla}
                onChange={(e) => handleEquipoChange(index, 'falla', e.target.value)}
                className="form-control"
                placeholder="Describa el problema o falla del equipo"
              />
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={agregarEquipo}
          className="btn btn-secondary"
        >
          + Agregar Otro Equipo
        </button>
      </div>

      {/* Observaciones y Anticipo (Fase 1) */}
      <div className="form-section">
        <h3 className="form-section-title">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          Observaciones y Anticipo
        </h3>
        <div className="form-group">
          <label className="form-label">Condición Física del Equipo</label>
          <textarea
            name="condicion_fisica"
            value={formData.condicion_fisica}
            onChange={handleChange}
            className="form-control"
            placeholder="Ej: Rayones en la tapa, falta un tornillo, pantalla con golpe en la esquina"
          />
        </div>
        <div className="form-group">
          <label className="form-label">Observaciones de Recepción</label>
          <textarea
            name="observaciones"
            value={formData.observaciones}
            onChange={handleChange}
            className="form-control"
            placeholder="Observaciones adicionales al recibir el equipo"
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Anticipo (Q)</label>
            <input
              type="number"
              name="anticipo"
              value={formData.anticipo}
              onChange={handleChange}
              className="form-control"
              step="0.01"
              min="0"
            />
          </div>
          {parseFloat(formData.anticipo) > 0 && (
            <div className="form-group">
              <label className="form-label">Fecha Anticipo</label>
              <input
                type="date"
                name="fecha_anticipo"
                value={formData.fecha_anticipo || formData.fecha_recepcion}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          )}
        </div>
      </div>

      {/* FASE 2: Revisión Técnico (solo visible si está editando o es técnico) */}
      {(isEditing || isTecnico) && (
        <div className="form-section">
          <h3 className="form-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
            Fase 2: Revisión Técnica
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Fecha de Revisión</label>
              <input
                type="date"
                name="fecha_revision"
                value={formData.fecha_revision || ''}
                onChange={handleChange}
                className="form-control"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-control"
              >
                <option value="pendiente">Pendiente</option>
                <option value="proceso">En Proceso</option>
                <option value="terminada">Terminada</option>
                <option value="entregada">Entregada</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Tipo de Servicio</label>
              <select
                name="tipo_servicio"
                value={formData.tipo_servicio}
                onChange={handleChange}
                className="form-control"
              >
                <option value="Revision">Revisión</option>
                <option value="Instalacion">Instalación</option>
                <option value="Mantenimiento">Mantenimiento</option>
                <option value="Actualizacion">Actualización</option>
                <option value="Llenado de tintas">Llenado de tintas</option>
                <option value="Otro">Otro</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Diagnóstico / Detalle del Trabajo</label>
            <textarea
              name="detalle_trabajo"
              value={formData.detalle_trabajo}
              onChange={handleChange}
              className="form-control"
              placeholder="Describa el diagnóstico y trabajo realizado"
            />
          </div>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="necesita_mercaderia"
                checked={formData.necesita_mercaderia}
                onChange={handleChange}
              />
              Necesita mercadería/repuestos
            </label>
          </div>
        </div>
      )}

      {/* FASE 3: Servicios, Mercadería y Repuestos */}
      {isEditing && (
        <>
          {/* Módulo de Servicios */}
          <div className="form-section">
            <h3 className="form-section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
              </svg>
              Fase 3: Servicios ({servicios.length})
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '16px' }}>
              Agregue los servicios prestados que no están relacionados con mercadería o repuestos.
            </p>

            {servicios.map((servicio, index) => (
              <div key={index} style={{
                background: '#fff',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <strong>Servicio {index + 1}</strong>
                  <button
                    type="button"
                    onClick={() => eliminarServicio(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Eliminar
                  </button>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label">Descripción del Servicio *</label>
                    <input
                      type="text"
                      value={servicio.descripcion}
                      onChange={(e) => handleServicioChange(index, 'descripcion', e.target.value)}
                      className="form-control"
                      placeholder="Ej: Instalación de Windows, Formateo completo"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cantidad</label>
                    <input
                      type="number"
                      value={servicio.cantidad}
                      onChange={(e) => handleServicioChange(index, 'cantidad', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Precio Unitario (Q)</label>
                    <input
                      type="number"
                      value={servicio.precio_unitario}
                      onChange={(e) => handleServicioChange(index, 'precio_unitario', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subtotal (Q)</label>
                    <input
                      type="number"
                      value={servicio.subtotal}
                      className="form-control"
                      disabled
                      style={{ backgroundColor: '#f8f9fa', fontWeight: '600' }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <input
                    type="text"
                    value={servicio.notas}
                    onChange={(e) => handleServicioChange(index, 'notas', e.target.value)}
                    className="form-control"
                    placeholder="Observaciones adicionales"
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={agregarServicio}
              className="btn btn-secondary"
            >
              + Agregar Servicio
            </button>
          </div>

          {/* Módulo de Mercaderías */}
          <div className="form-section">
            <h3 className="form-section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <circle cx="9" cy="21" r="1"/>
                <circle cx="20" cy="21" r="1"/>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
              </svg>
              Mercadería ({mercaderias.length})
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '16px' }}>
              Agregue detalladamente la mercadería vendida al cliente.
            </p>

            {mercaderias.map((mercaderia, index) => (
              <div key={index} style={{
                background: '#fff',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <strong>Mercadería {index + 1}</strong>
                  <button
                    type="button"
                    onClick={() => eliminarMercaderia(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Eliminar
                  </button>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label">Descripción *</label>
                    <input
                      type="text"
                      value={mercaderia.descripcion}
                      onChange={(e) => handleMercaderiaChange(index, 'descripcion', e.target.value)}
                      className="form-control"
                      placeholder="Ej: Mouse inalámbrico, Teclado USB"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cantidad</label>
                    <input
                      type="number"
                      value={mercaderia.cantidad}
                      onChange={(e) => handleMercaderiaChange(index, 'cantidad', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Precio Venta (Q)</label>
                    <input
                      type="number"
                      value={mercaderia.precio_unitario}
                      onChange={(e) => handleMercaderiaChange(index, 'precio_unitario', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subtotal (Q)</label>
                    <input
                      type="number"
                      value={mercaderia.subtotal}
                      className="form-control"
                      disabled
                      style={{ backgroundColor: '#f8f9fa', fontWeight: '600' }}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Notas</label>
                  <input
                    type="text"
                    value={mercaderia.notas}
                    onChange={(e) => handleMercaderiaChange(index, 'notas', e.target.value)}
                    className="form-control"
                    placeholder="Observaciones adicionales"
                  />
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={agregarMercaderia}
              className="btn btn-secondary"
            >
              + Agregar Mercadería
            </button>
          </div>

          {/* Módulo de Repuestos */}
          <div className="form-section">
            <h3 className="form-section-title">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21 15 16 10 5 21"/>
              </svg>
              Repuestos ({repuestos.length})
            </h3>
            <p style={{ color: 'var(--text-light)', fontSize: '13px', marginBottom: '16px' }}>
              Agregue detalladamente los repuestos utilizados en la reparación.
            </p>

            {repuestos.map((repuesto, index) => (
              <div key={index} style={{
                background: '#fff',
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <strong>Repuesto {index + 1}</strong>
                  <button
                    type="button"
                    onClick={() => eliminarRepuesto(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Eliminar
                  </button>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label">Descripción *</label>
                    <input
                      type="text"
                      value={repuesto.descripcion}
                      onChange={(e) => handleRepuestoChange(index, 'descripcion', e.target.value)}
                      className="form-control"
                      placeholder="Ej: Memoria RAM 8GB, Disco duro 1TB"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Cantidad</label>
                    <input
                      type="number"
                      value={repuesto.cantidad}
                      onChange={(e) => handleRepuestoChange(index, 'cantidad', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Precio Compra (Q)</label>
                    <input
                      type="number"
                      value={repuesto.precio_compra}
                      onChange={(e) => handleRepuestoChange(index, 'precio_compra', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                      title="Costo de adquisición (interno)"
                    />
                    <small style={{ fontSize: '10px', color: 'var(--text-light)' }}>Interno</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Precio Venta (Q)</label>
                    <input
                      type="number"
                      value={repuesto.precio_venta}
                      onChange={(e) => handleRepuestoChange(index, 'precio_venta', e.target.value)}
                      className="form-control"
                      step="0.01"
                      min="0"
                      title="Precio cobrado al cliente"
                    />
                    <small style={{ fontSize: '10px', color: 'var(--text-light)' }}>Cliente</small>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subtotal Venta (Q)</label>
                    <input
                      type="number"
                      value={repuesto.subtotal_venta}
                      className="form-control"
                      disabled
                      style={{ backgroundColor: '#f8f9fa', fontWeight: '600' }}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Lugar de Cotización</label>
                    <input
                      type="text"
                      value={repuesto.lugar_cotizacion}
                      onChange={(e) => handleRepuestoChange(index, 'lugar_cotizacion', e.target.value)}
                      className="form-control"
                      placeholder="Ej: Proveedor ABC"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fecha de Ingreso</label>
                    <input
                      type="date"
                      value={repuesto.fecha_ingreso}
                      onChange={(e) => handleRepuestoChange(index, 'fecha_ingreso', e.target.value)}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label">Notas</label>
                    <input
                      type="text"
                      value={repuesto.notas}
                      onChange={(e) => handleRepuestoChange(index, 'notas', e.target.value)}
                      className="form-control"
                      placeholder="Observaciones adicionales"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={agregarRepuesto}
              className="btn btn-secondary"
            >
              + Agregar Repuesto
            </button>
          </div>
        </>
      )}

      {/* FASE 4: Pago (solo si está editando) */}
      {isEditing && (
        <div className="form-section">
          <h3 className="form-section-title">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <line x1="12" y1="1" x2="12" y2="23"/>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
            </svg>
            Fase 4: Costos y Pago
          </h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Costo del Trabajo (Q)</label>
              <input
                type="number"
                name="costo_trabajo"
                value={formData.costo_trabajo}
                onChange={handleChange}
                className="form-control"
                step="0.01"
                min="0"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Tipo de Pago</label>
              <select
                name="tipo_pago"
                value={formData.tipo_pago}
                onChange={handleChange}
                className="form-control"
              >
                <option value="Pendiente">Pendiente</option>
                <option value="Efectivo">Efectivo</option>
                <option value="Transferencia">Transferencia</option>
                <option value="Tarjeta">Tarjeta</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Fecha de Entrega</label>
              <input
                type="date"
                name="fecha_entrega"
                value={formData.fecha_entrega}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          {/* Totales */}
          <div style={{
            background: 'white',
            padding: '16px',
            borderRadius: '8px',
            marginTop: '16px',
            display: 'flex',
            gap: '32px',
            justifyContent: 'flex-end'
          }}>
            <div>
              <span style={{ color: 'var(--text-light)', marginRight: '8px' }}>TOTAL:</span>
              <strong style={{ fontSize: '20px' }}>Q{valorTotal.toLocaleString('es-GT', { minimumFractionDigits: 2 })}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-light)', marginRight: '8px' }}>SALDO:</span>
              <strong style={{ fontSize: '20px', color: saldo > 0 ? 'var(--danger-color)' : 'var(--success-color)' }}>
                Q{saldo.toLocaleString('es-GT', { minimumFractionDigits: 2 })}
              </strong>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
        <button type="button" className="btn btn-secondary" onClick={() => window.history.back()}>
          Cancelar
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading}>
          {loading ? 'Guardando...' : (isEditing ? 'Guardar Cambios' : 'Recibir Equipo')}
        </button>
      </div>
    </form>
  );
};

export default OrdenForm;
